import { Injectable } from "@angular/core";
import { Action, Selector, State, StateContext } from "@ngxs/store";
import { GetPlacementExternshipDocumentsResponse } from "@platform-app/app/core/api/models";
import {
  PlacementsService,
  StudentService,
} from "@platform-app/app/core/api/services";
import {
  DeleteExternshipDocument,
  LoadPlacementExternshipDocuments,
  LoadStudentExternshipDocuments,
  ResetExternshipDocuments,
  UploadExternshipDocument,
} from "@platform-app/app/shared/externship-documents/externship-documents.actions";
import { finalize, Observable, switchMap, tap } from "rxjs";

export interface ExternshipDocumentsStateModel {
  documents: GetPlacementExternshipDocumentsResponse | null;
  dataLoading: boolean;
  uploadingSlots: string[];
}

const DEFAULT_EXTERNSHIP_DOCUMENTS_STATE: ExternshipDocumentsStateModel = {
  documents: null,
  dataLoading: false,
  uploadingSlots: [],
};

@State<ExternshipDocumentsStateModel>({
  name: "ExternshipDocuments",
  defaults: DEFAULT_EXTERNSHIP_DOCUMENTS_STATE,
})
@Injectable()
export class ExternshipDocumentsState {
  constructor(
    private placementsService: PlacementsService,
    private studentService: StudentService,
  ) {}

  @Selector()
  static documents(state: ExternshipDocumentsStateModel) {
    return state.documents;
  }

  @Selector()
  static slots(state: ExternshipDocumentsStateModel) {
    return state.documents?.slots ?? [];
  }

  @Selector()
  static isAvailable(state: ExternshipDocumentsStateModel) {
    return state.documents?.isAvailable ?? false;
  }

  @Selector()
  static canEdit(state: ExternshipDocumentsStateModel) {
    return state.documents?.placementId ? state.documents.canEdit : false;
  }

  @Selector()
  static allDocumentsUploaded(state: ExternshipDocumentsStateModel) {
    return state.documents?.allDocumentsUploaded ?? false;
  }

  @Selector()
  static dataLoading(state: ExternshipDocumentsStateModel) {
    return state.dataLoading;
  }

  @Selector()
  static uploadingSlots(state: ExternshipDocumentsStateModel) {
    return state.uploadingSlots;
  }

  @Action(LoadPlacementExternshipDocuments, { cancelUncompleted: true })
  loadPlacementExternshipDocuments(
    ctx: StateContext<ExternshipDocumentsStateModel>,
    action: LoadPlacementExternshipDocuments,
  ) {
    return this.load(
      ctx,
      this.placementsService.getPlacementExternshipDocuments({
        id: action.payload.placementId,
      }),
    );
  }

  @Action(LoadStudentExternshipDocuments, { cancelUncompleted: true })
  loadStudentExternshipDocuments(
    ctx: StateContext<ExternshipDocumentsStateModel>,
    action: LoadStudentExternshipDocuments,
  ) {
    return this.load(
      ctx,
      this.studentService.getStudentExternshipDocuments({
        id: action.payload.studentId,
      }),
    );
  }

  @Action(UploadExternshipDocument)
  uploadExternshipDocument(
    ctx: StateContext<ExternshipDocumentsStateModel>,
    action: UploadExternshipDocument,
  ) {
    const placementId = ctx.getState().documents?.placementId;
    if (!placementId) return;

    const { slot, file } = action.payload;

    ctx.patchState({
      uploadingSlots: [...ctx.getState().uploadingSlots, slot],
    });

    return this.placementsService
      .uploadPlacementExternshipDocument({
        id: placementId,
        slot,
        body: { file },
      })
      .pipe(
        switchMap(() => this.reload(ctx, placementId)),
        finalize(() =>
          ctx.patchState({
            uploadingSlots: ctx
              .getState()
              .uploadingSlots.filter((s) => s !== slot),
          }),
        ),
      );
  }

  @Action(DeleteExternshipDocument)
  deleteExternshipDocument(
    ctx: StateContext<ExternshipDocumentsStateModel>,
    action: DeleteExternshipDocument,
  ) {
    const placementId = ctx.getState().documents?.placementId;
    if (!placementId) return;

    return this.placementsService
      .deletePlacementExternshipDocument({
        id: placementId,
        slot: action.payload.slot,
      })
      .pipe(switchMap(() => this.reload(ctx, placementId)));
  }

  @Action(ResetExternshipDocuments)
  resetExternshipDocuments(ctx: StateContext<ExternshipDocumentsStateModel>) {
    ctx.setState(DEFAULT_EXTERNSHIP_DOCUMENTS_STATE);
  }

  private load(
    ctx: StateContext<ExternshipDocumentsStateModel>,
    request$: Observable<GetPlacementExternshipDocumentsResponse>,
  ) {
    ctx.setState({ ...DEFAULT_EXTERNSHIP_DOCUMENTS_STATE, dataLoading: true });

    return request$.pipe(
      tap((documents) => ctx.patchState({ documents })),
      finalize(() => ctx.patchState({ dataLoading: false })),
    );
  }

  private reload(
    ctx: StateContext<ExternshipDocumentsStateModel>,
    placementId: string,
  ) {
    return this.placementsService
      .getPlacementExternshipDocuments({ id: placementId })
      .pipe(tap((documents) => ctx.patchState({ documents })));
  }
}
